import { apiClient } from '@/api/client'
import type { Message } from '@/types'

// ─── Types ────────────────────────────────────────────────────────────

/** bot = AI/rules reply · waiting = needs the team · open = team replying ·
 *  resolved = solved. */
export type InboxChatStatus = 'bot' | 'waiting' | 'open' | 'resolved'

export interface InboxAssignee {
  id: number
  name: string
}

export interface InboxStateSummary {
  status: InboxChatStatus
  assigned_to: InboxAssignee | null
  handoff_reason: string
}

export interface InboxState extends InboxStateSummary {
  id: number
  conversation_id: string
  customer_id: number | null
  handoff_note: string
  last_customer_at: string | null
  resolved_at: string | null
}

export interface InboxContact {
  id: number
  first_name: string
  last_name: string
  full_name: string
  /** Empty string for BSUID-only contacts (usernames rollout, 2026). */
  phone_number: string
  /** Meta Business-Scoped User ID — present for username-era contacts. */
  bsuid?: string
  /** WhatsApp username (without '@'). Display only. */
  username?: string
  email: string | null
  is_active: boolean
  last_message_at: string | null
  last_message_preview: string
  last_message_direction: string
  /** Delivery status of the last OUTBOUND message (sent/delivered/read/failed). */
  last_message_status?: string
  unread_count: number
  /** Customer messaged last — still waiting on a reply. */
  awaiting_reply?: boolean
  /** Inbox prefs (stored in Customer.meta_info). */
  muted?: boolean
  pinned?: boolean
  tags: string[]
  /** False for a searched customer with no messages on the selected phone
   *  ("Other customers"): the chat opens empty and must start with a
   *  template. Absent on older backends → treat as true. */
  has_conversation?: boolean
  /** Inbox hand-over state (who is handling the chat). Absent on
   *  no-conversation rows and older backends → treat as 'bot'. */
  inbox_state?: InboxStateSummary
  /** On the suppression list — every send is refused (no-conversation rows). */
  suppressed?: boolean
  /** Opted out of marketing (STOP) — utility templates still allowed. */
  marketing_opted_out?: boolean
}

export interface InboxContactMedia {
  media: {
    id: string
    media_url: string
    media_type: string
    media_caption: string
    direction: string
    created_at: string | null
  }[]
  links: {
    id: string
    text: string
    direction: string
    created_at: string | null
  }[]
}

export interface InboxContactsResponse {
  contacts: InboxContact[]
  page: number
  page_size: number
  has_next: boolean
  /** AI assistant is on (AI badges / filter / "Hand back to AI" show only then). */
  ai_active?: boolean
  /** Only with `search` + `include_without_messages` (never on Unread):
   *  matching customers with NO conversation on the selected phone. */
  other_contacts?: InboxContact[]
  other_has_next?: boolean
  other_page?: number
  other_page_size?: number
}

export interface InboxMessagesResponse {
  messages: InboxMessage[]
  page: number
  page_size: number
  has_next: boolean
  /** The thread's pinned messages, newest first — on every page, so the
   *  pinned bar can preview (and page back to) one that isn't loaded yet. */
  pinned?: PinnedMessageSummary[]
  customer: {
    id: number
    full_name: string
    phone_number: string
    bsuid?: string
    username?: string
  }
}

/** Light summary of a pinned message (see InboxMessagesResponse.pinned). */
export interface PinnedMessageSummary {
  id: string
  message_id: string
  direction: 'inbound' | 'outbound'
  message_type: string
  content: string
  media_url?: string | null
  media_type?: string | null
  media_caption?: string | null
  template_name?: string | null
  location_name?: string | null
  created_at: string | null
}

export interface InboxMessage {
  id: string
  message_id: string
  direction: 'inbound' | 'outbound'
  message_type: string
  content: string
  /** Emoji reactions on this message ({business, customer}). */
  reactions?: { business?: string; customer?: string } | null
  /** Inbox-only flags (starred / pinned). */
  flags?: { starred?: boolean; pinned?: boolean } | null
  /** Quoted message (reply context). */
  context?: {
    wamid: string
    direction: string
    content: string
    media_type: string
  } | null
  media_url?: string | null
  media_type?: string | null
  media_caption?: string | null
  /** Name the customer sees on a document (null → derive from the URL). */
  media_filename?: string | null
  template_name?: string | null
  // Real template content (header media/text + body + buttons) so a campaign
  // template renders as its card instead of "[Template: name]".
  template?: Message['template']
  // Normalized interactive payload (reply buttons / list).
  interactive?: Message['interactive']
  /** Voice call log row (message_type 'call'). */
  call?: Message['call']
  // Snapshot of a sent carousel template's cards (image/body/buttons per card).
  carousel?: Message['carousel']
  // Snapshot of a sent LOCATION-header template's place (lat/lng/name/address).
  location?: Message['location']
  // Meta message id — react/reply target and quoted-context lookups.
  wamid?: string | null
  /** Forward menu state (null → hidden; allowed:false → disabled + reason). */
  forward?: Message['forward']
  /** Sent via Forward — the bubble shows a "Forwarded" label. */
  forwarded?: boolean
  status: string
  error_code?: string | null
  error_message?: string | null
  sent_at?: string | null
  delivered_at?: string | null
  read_at?: string | null
  created_at: string | null
}

/** One recipient's outcome from `forwardMessage`. */
export interface ForwardRecipientResult {
  contact_id: number
  name: string
  status: 'sent' | 'failed'
  message_id?: string
  /** Human-readable failure reason (e.g. "24-hour window closed — …"). */
  reason?: string
}

export interface ForwardMessageResponse {
  /** Server summary, e.g. "Sent to 4 of 5 — Romin: 24-hour window closed…". */
  message: string
  sent: number
  failed: number
  total: number
  results: ForwardRecipientResult[]
}

/** What the inbox send endpoints return (`data` of the envelope). */
export interface InboxSendResult {
  /** Meta wamid (= the thread row's id). */
  message_id?: string | null
  customer_id?: number
  /** The sent message's thread row — same shape as a messages-endpoint row,
   *  so it drops straight into the open thread. Absent on older backends. */
  message?: InboxMessage | null
}

/** A send's thread row from a success body, or from a failed send's error
 *  (`ApiError.data.message` — the row the server persisted as failed). */
export function sentMessageFrom(result: unknown): Message | null {
  const row = (result as { message?: unknown } | null | undefined)?.message
  if (!row || typeof row !== 'object' || !(row as InboxMessage).id) return null
  return mapToMessage(row as InboxMessage)
}

export interface InboxCustomerDetail {
  id: number
  first_name: string
  last_name: string
  full_name: string
  phone_number: string
  email: string
  is_active: boolean
  tags: string[]
  meta_info: Record<string, unknown>
  created_at: string
  updated_at: string
  last_message_at: string | null
  bsuid?: string
  username?: string
  /** Any traffic on the SELECTED phone (false → empty "start with a
   *  template" chat). Absent on older backends. */
  has_conversation?: boolean
}

// ─── Map to existing Message type (for backward compat) ──────────────

export function mapToMessage(raw: InboxMessage): Message {
  return {
    id: raw.id,
    message_id: raw.message_id,
    conversation_id: '',
    direction: raw.direction,
    message_type: (raw.message_type || 'text') as Message['message_type'],
    content: raw.content,
    reactions: raw.reactions ?? undefined,
    flags: raw.flags ?? undefined,
    context: raw.context ?? undefined,
    media_url: raw.media_url ?? undefined,
    media_type: raw.media_type ?? undefined,
    media_caption: raw.media_caption ?? undefined,
    media_filename: raw.media_filename ?? undefined,
    template_name: raw.template_name ?? undefined,
    template: raw.template ?? undefined,
    interactive: raw.interactive ?? undefined,
    call: raw.call ?? undefined,
    carousel: raw.carousel ?? undefined,
    location: raw.location ?? undefined,
    wamid: raw.wamid ?? undefined,
    forward: raw.forward ?? null,
    forwarded: !!raw.forwarded,
    status: raw.status as Message['status'],
    error_code: raw.error_code ?? undefined,
    error_message: raw.error_message ?? undefined,
    sent_at: raw.sent_at ?? undefined,
    delivered_at: raw.delivered_at ?? undefined,
    read_at: raw.read_at ?? undefined,
    created_at: raw.created_at ?? '',
  }
}

// ─── API Calls ────────────────────────────────────────────────────────

export const inboxApi = {
  /** Paginated contact list sorted by last_message_at */
  getContacts: (
    wabaId: string,
    params?: {
      search?: string
      has_unread?: boolean
      /** Only chats where the customer messaged last (read or not). */
      awaiting_reply?: boolean
      page?: number
      page_size?: number
      /** Hand-over filter; 'mine' = waiting/open chats assigned to me. */
      status?: InboxChatStatus | 'mine'
      /** With `search`: also return `other_contacts` (no-conversation matches). */
      include_without_messages?: boolean
      /** With include_without_messages: skip the conversation list. */
      other_only?: boolean
      other_page?: number
      other_page_size?: number
    },
  ) =>
    apiClient
      .get<{ status: boolean; data: InboxContactsResponse }>(
        `/api/crm/${wabaId}/inbox/contacts/`,
        { params },
      )
      .then((r) => r.data.data),

  /** Per-contact inbox prefs: mute / pin / clear chat (soft-clear the thread). */
  setContactPrefs: (
    wabaId: string,
    customerId: number,
    prefs: { muted?: boolean; pinned?: boolean; clear_chat?: boolean },
  ) =>
    apiClient
      .post<{
        status: boolean
        data: { muted: boolean; pinned: boolean; cleared_before: string | null }
      }>(`/api/crm/${wabaId}/inbox/contacts/${customerId}/prefs/`, prefs)
      .then((r) => r.data.data),

  /** Media / links / docs shared in this chat (Contact info panel). */
  getContactMedia: (wabaId: string, customerId: number) =>
    apiClient
      .get<{ status: boolean; data: InboxContactMedia }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/media/`,
      )
      .then((r) => r.data.data),

  /** Number of contacts with unread messages — drives the sidebar Inbox badge. */
  getUnreadCount: (wabaId: string) =>
    apiClient
      .get<{ status: boolean; data: { count: number } }>(
        `/api/crm/${wabaId}/inbox/unread-count/`,
      )
      .then((r) => r.data.data.count),

  /** Paginated message history for a customer (newest first) */
  getMessages: (
    wabaId: string,
    customerId: number,
    params?: { page?: number; page_size?: number; strict_phone?: boolean },
  ) =>
    apiClient
      .get<{ status: boolean; data: InboxMessagesResponse }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/messages/`,
        { params },
      )
      .then((r) => {
        const d = r.data.data
        return {
          ...d,
          mappedMessages: d.messages.map(mapToMessage),
        }
      }),

  /** Hand-over state of one chat */
  getState: (wabaId: string, customerId: number) =>
    apiClient
      .get<{ status: boolean; data: InboxState }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/state/`,
      )
      .then((r) => r.data.data),

  /** Assign to me / hand back to AI / mark solved */
  stateAction: (
    wabaId: string,
    customerId: number,
    action: 'assign-to-me' | 'back-to-ai' | 'resolve',
  ) =>
    apiClient
      .post<{
        status: boolean
        message?: string
        data: InboxState
        /** Only on resolve: did the CSAT survey go out, and if not why. */
        csat?: { sent: boolean; reason: string; message: string }
      }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/${action}/`,
      )
      .then((r) => r.data),

  /** Transfer the chat to another team member */
  transfer: (wabaId: string, customerId: number, profileId: number) =>
    apiClient
      .post<{ status: boolean; message?: string; data: InboxState }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/transfer/`,
        { profile_id: profileId },
      )
      .then((r) => r.data),

  /** Team members a chat can be transferred to */
  getTeam: (wabaId: string) =>
    apiClient
      .get<{ status: boolean; data: InboxAssignee[] }>(`/api/crm/${wabaId}/inbox/team/`)
      .then((r) => r.data.data),

  /** Mark all messages as read for a customer */
  markRead: (wabaId: string, customerId: number) =>
    apiClient
      .post(`/api/crm/${wabaId}/inbox/contacts/${customerId}/mark-read/`)
      .then((r) => r.data),

  /** Get customer detail */
  getCustomerDetail: (wabaId: string, customerId: number) =>
    apiClient
      .get<{ status: boolean; data: InboxCustomerDetail }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/`,
      )
      .then((r) => r.data.data),

  /** Update customer details */
  updateCustomer: (
    wabaId: string,
    customerId: number,
    data: { first_name?: string; last_name?: string; email?: string },
  ) =>
    apiClient
      .patch(`/api/crm/${wabaId}/inbox/contacts/${customerId}/`, data)
      .then((r) => r.data),

  /** Add a tag */
  addTag: (wabaId: string, customerId: number, tag: string) =>
    apiClient
      .post<{ status: boolean; data: { tags: string[] } }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/tags/`,
        { tag },
      )
      .then((r) => r.data.data.tags),

  /** Remove a tag */
  removeTag: (wabaId: string, customerId: number, tag: string) =>
    apiClient
      .delete<{ status: boolean; data: { tags: string[] } }>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/tags/${encodeURIComponent(tag)}/`,
      )
      .then((r) => r.data.data.tags),

  /** Send a text message */
  sendTextMessage: (
    wabaId: string,
    data: {
      phone_number?: string
      contact_id?: string
      customer_id?: string | number
      message: string
      /** wamid of the message being replied to (quoted reply). */
      context_message_id?: string
    },
  ) =>
    apiClient
      .post<{ success: boolean; data?: InboxSendResult }>(`/api/${wabaId}/send_message/`, {
        ...data,
        customer_id: data.customer_id ? Number(data.customer_id) : undefined,
      })
      .then((r) => r.data),

  /** React to a customer's message with an emoji ('' removes the reaction). */
  reactToMessage: (
    wabaId: string,
    customerId: number,
    data: { wamid: string; emoji: string },
  ) =>
    apiClient
      .post(`/api/crm/${wabaId}/inbox/contacts/${customerId}/react/`, data)
      .then((r) => r.data),

  /** Star / pin / locally-delete a message (flags in log.data.inbox_flags). */
  setMessageFlags: (
    wabaId: string,
    messageId: string,
    flags: { starred?: boolean; pinned?: boolean; deleted?: boolean },
  ) =>
    apiClient
      .post(
        `/api/crm/${wabaId}/inbox/messages/${encodeURIComponent(messageId)}/flags/`,
        flags,
      )
      .then((r) => r.data),

  /**
   * Forward one message to other contacts. The server copies the original
   * content (media included — nothing is re-uploaded from the browser) and
   * sends it from this phone number; one recipient failing (e.g. a closed
   * 24-hour window) doesn't block the others — see `results`.
   */
  forwardMessage: (wabaId: string, messageId: string, contactIds: number[]) =>
    apiClient
      .post<{
        status: boolean
        message: string
        data: Omit<ForwardMessageResponse, 'message'>
      }>(`/api/crm/${wabaId}/inbox/messages/${encodeURIComponent(messageId)}/forward/`, {
        contact_ids: contactIds,
      })
      .then((r): ForwardMessageResponse => ({ ...r.data.data, message: r.data.message })),

  /** Send a template message.
   *
   *  Carousel card media is sent as a URL, never as an upload: the caller
   *  uploads each card's file via `uploadMedia` first and puts the resulting
   *  public URL in `template_cards`. Uploading here instead would only yield a
   *  Meta media id, which sends fine but can't be rendered in the inbox bubble.
   */
  sendTemplateMessage: (
    wabaId: string,
    data: {
      phone_number: string
      template_name: string
      language: string
      components?: Record<string, unknown>[]
      /** Carousel templates only: one entry per card ({header, "1", "2", …}).
       *  The backend builds the cards components from this — the card media
       *  must be supplied per send, the template's sample handle isn't sendable. */
      template_cards?: Record<string, string>[]
      /** Send-time button values, keyed by the button's index in the template's
       *  BUTTONS component. Only a dynamic URL button's suffix, a coupon code
       *  and a quick-reply payload can vary per send — Meta locks everything
       *  else at approval. Carousel card buttons go via `template_cards`. */
      button_values?: Record<string, string>
    },
  ) => {
    const url = `/api/${wabaId}/send_media/`

    return apiClient
      .post(url, {
        phone_number: data.phone_number,
        message_type: 'template',
        template_name: data.template_name,
        template_language: data.language,
        template_components: data.components,
        template_cards: data.template_cards,
        button_values: data.button_values,
      })
      .then((r) => r.data)
  },

  /**
   * Render personalized text onto a template header image for one customer
   * (same overlay engine as campaign header images). Returns the rendered
   * image URL to use as the header `link`.
   */
  renderHeaderImage: (
    wabaId: string,
    data: {
      phone_number: string
      base_image_url: string
      overlay: Record<string, unknown>
    },
  ) =>
    apiClient
      .post(`/api/${wabaId}/inbox/render-header-image/`, data)
      .then((r) => r.data.data as { url: string; customer_found: boolean }),

  /** Upload a media file → returns a public URL to use as a send `link`. */
  uploadMedia: (wabaId: string, file: File, category = 'inbox_media') => {
    const form = new FormData()
    form.append('file', file)
    form.append('category', category)
    // Unset Content-Type so Axios auto-sets multipart/form-data WITH the
    // boundary when it detects FormData. The apiClient default of
    // 'application/json' otherwise serializes the file to `{}` → 415.
    return apiClient
      .post(`/api/${wabaId}/media/upload/`, form, {
        headers: { 'Content-Type': undefined },
      })
      .then(
        (r) => r.data.data as { url: string; key: string; category: string },
      )
  },

  /** Copy a Setu Media library file into public media (server-side, by
   *  asset id) → returns a public URL, same response as `uploadMedia`. */
  uploadMediaFromLibrary: (wabaId: string, mediaAssetId: number, category = 'inbox_media') => {
    const form = new FormData()
    form.append('media_asset_id', String(mediaAssetId))
    form.append('category', category)
    return apiClient
      .post(`/api/${wabaId}/media/upload/`, form, {
        headers: { 'Content-Type': undefined },
      })
      .then(
        (r) => r.data.data as { url: string; key: string; category: string },
      )
  },

  /**
   * Send an interactive message the agent built in the inbox — reply buttons
   * (max 3) or a "Select an option" list. Hits the same /send_media/ endpoint
   * which builds the Meta interactive payload and logs it so it renders back.
   */
  sendInteractiveMessage: (
    wabaId: string,
    data: {
      phone_number?: string
      customer_id?: string | number
      message_type: 'interactive_buttons' | 'interactive_list'
      message: string
      header_text?: string
      footer_text?: string
      // interactive_buttons
      buttons?: { id: string; title: string }[]
      // interactive_list
      button_text?: string
      list_sections?: {
        title: string
        rows: { id: string; title: string; description?: string }[]
      }[]
    },
  ) =>
    apiClient
      .post(`/api/${wabaId}/send_media/`, {
        ...data,
        customer_id: data.customer_id ? Number(data.customer_id) : undefined,
      })
      .then((r) => r.data),

  /** Send a media message (image / video / audio / document / sticker) by
   *  public URL. `filename` is what the customer sees on a document. */
  sendMediaMessage: (
    wabaId: string,
    data: {
      phone_number?: string
      customer_id?: string | number
      message_type: 'image' | 'video' | 'audio' | 'document' | 'sticker'
      /** Public URL of the file (omit when sending a Setu Media file). */
      link?: string
      /** Setu Media library file — sent from storage by (cached) Meta media
       *  id; the backend fills the URL and the document's name. */
      media_asset_id?: number
      filename?: string
      caption?: string
    },
  ) =>
    apiClient
      .post<{ success: boolean; data?: InboxSendResult }>(`/api/${wabaId}/send_media/`, {
        phone_number: data.phone_number,
        customer_id: data.customer_id ? Number(data.customer_id) : undefined,
        message_type: data.message_type,
        // The view reads `media_url` from the raw request; `link` is sent too
        // for the serializer field's sake.
        media_url: data.link,
        link: data.link,
        media_asset_id: data.media_asset_id,
        filename: data.filename,
        caption: data.caption,
      })
      .then((r) => r.data),
}
